import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Fade,
  Paper,
  Divider
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import LoadingImage from '../../components/LoadingImage';

const DoubleDeckDorseKapasite: React.FC = () => {
  useEffect(() => {
    document.title = "Çift Katlı Dorse (Double Deck) Sistemleri ve Kapasite Artırma - Kale Treyler";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Mersin'de tır dorselerinde çift kat (double deck) sistemi kurulumu. Yük taşıma kapasitesini artırma ve dorse tadilat süreçlerine dair teknik detaylar.");
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "double deck çift kat dorse montajı, Mersin dorse tadilat, dorse taşıma kapasitesi artırma, tır çift katlı sistem fiyatı");
    }

    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/blog/double-deck-dorse-tasima-kapasitesi');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  return (
    <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box>
            <Button
              component={RouterLink}
              to="/blog"
              startIcon={<ArrowBack />}
              sx={{ mb: 4, textTransform: 'none', fontWeight: 'bold' }}
            >
              Blog Yazılarına Dön
            </Button>

            <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: '1px solid', borderColor: 'grey.200', borderRadius: 4 }}>
              <Typography variant="overline" color="secondary" sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}>
                Double Deck
              </Typography>
              <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800, mt: 1, mb: 4, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.2 }}>
                Çift Katlı Dorse (Double Deck) Sistemleri ile Taşıma Kapasitesi Nasıl Artırılır?
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Yayınlanma Tarihi: <strong>12 Temmuz 2026</strong>
                </Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  Yazar: <strong>Kale Treyler Editör</strong>
                </Typography>
              </Box>

              <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                <LoadingImage
                  src="/24.jpg"
                  alt="Dorse İçi Double Deck Çift Kat Sistemi Montajı"
                  style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'cover' }}
                />
              </Box>

              <Box sx={{ typography: 'body1', color: 'text.primary', lineHeight: 1.8 }}>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Lojistik firmaları ve bireysel tır sahipleri için her taşıma seferinde maksimum yük hacmine ulaşmak, sefer maliyetlerini düşürmenin en etkili yoludur. Özellikle paletli ve üst üste istiflenemeyen hassas yüklerin taşınmasında dorsenin üst kısmındaki geniş hacim boş kalmaktadır. Bu sorunu çözmek için geliştirilen <RouterLink to="/hizmetler/double-deck" style={{ color: '#1e3a8a', fontWeight: 'bold', textDecoration: 'underline' }}>double deck sistem</RouterLink> uygulamaları, dorsenizde ikinci bir yükleme katı oluşturarak hacmi ikiye katlamanıza olanak tanır.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Double Deck (Çift Kat) Sisteminin Çalışma Mantığı
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Double deck sistem, dorse kasa içi yan duvarlarına monte edilen dikey profiller ve bu profiller üzerinde hareket edebilen yatay kirişlerden oluşur. Yükleme esnasında ilk kat yerleştirildikten sonra kirişler istenilen yüksekliğe ayarlanır ve üzerine özel çift kat paletleri konularak ikinci yükleme katı oluşturulur. Bu sistem dorse kasa yapısına zarar vermeden entegre edilir. Profesyonelce yapılan <strong>double deck çift kat dorse montajı</strong> sayesinde, tır dorsesinin yüksekliği boyunca atıl kalan tüm hacim aktif taşıma alanına dönüştürülür.
                </Typography>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Mersin'de Dorse Tadilat ve Double Deck Uygulamaları
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                  Mersin'de Akdeniz lojistik bölgesindeki atölyemizde tır ve dorseler için özel modifikasyon çözümleri sunuyoruz. Uyguladığımız <strong>Mersin dorse tadilat</strong> işlemleri, uluslararası standartlara uygun malzemelerle gerçekleştirilmektedir. Dorse içerisine kurulacak çift katlı yapının montajı aşamasında şu kriterlere önem veriyoruz:
                </Typography>
                <ul>
                  <li>Dorse yan duvarlarının taşıyıcı mukavemetinin ölçülmesi ve güçlendirilmesi.</li>
                  <li>Hafif ama yüksek mukavemetli alüminyum ve çelik alaşımlı kirişlerin kullanılması (aracın boş ağırlığını artırmamak için).</li>
                  <li>Kirişlerin kolayca sökülüp takılabilir ve yük yüksekliğine göre ayarlanabilir yapıda tasarlanması.</li>
                </ul>

                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mt: 4, mb: 2, color: 'primary.main' }}>
                  Hacim Odaklı Yüklerde Sefer Başına Tasarruf
                </Typography>
                <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                  Hacimli ancak görece hafif yük taşımacılığı yapan firmalar, normal şartlarda iki araçla sevk edecekleri paletli malzemeleri çift kat sistemi kurulmuş tek bir dorse ile taşıyabilmektedir. Bu sayede yakıt, otoban geçiş ücretleri, şoför maliyetleri ve araç yıpranma payı gibi işletme giderlerinde önemli seviyelerde maliyet optimizasyonu sağlanmaktadır. Mersin bölgesinde dorse modifikasyonu arayışında olan nakliye profesyonellerine sunduğumuz <strong>dorse taşıma kapasitesi artırma</strong> çözümleri ile taşıma süreçlerinizi daha verimli hale getirebilirsiniz.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, py: 1, bgcolor: 'grey.100' }}>
                  Not: Sitemizde yer alan tüm blog içerikleri lojistik sektörü çalışanları için genel bilgilendirme amacıyla hazırlanmıştır. Aracınızın tescil ve yük taşıma standartlarına uygun modifikasyon işlemleri için lütfen teknik ekibimizle iletişime geçiniz.
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default DoubleDeckDorseKapasite;
